import { ChevronRight, Download, Play, Save, Sparkles } from 'lucide-react-native';
import { Fragment, type ReactNode } from 'react';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { AnimatedCard } from '../../../components/animations';
import { HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { NodeType } from './WorkflowNode';

interface VisualizerNode {
  id: string;
  type: NodeType;
  label: string;
}

interface WorkflowVisualizerProps {
  name: string;
  description: string;
  nodes?: VisualizerNode[];
  delay?: number;
}

const defaultNodeKeys = [
  {
    id: 'trigger',
    type: 'trigger' as NodeType,
    labelKey: 'workflow.visualizer.nodes.manualTrigger',
  },
  { id: 'fetch', type: 'action' as NodeType, labelKey: 'workflow.visualizer.nodes.fetchProfile' },
  { id: 'enrich', type: 'action' as NodeType, labelKey: 'workflow.visualizer.nodes.enrichData' },
  { id: 'save', type: 'output' as NodeType, labelKey: 'workflow.visualizer.nodes.updateLead' },
];

const nodeIcons: Record<string, ReactNode> = {
  trigger: <Play size={18} color={colors.primary.neon} strokeWidth={2.2} />,
  fetch: <Download size={18} color={colors.accent.blue[400]} strokeWidth={2.2} />,
  enrich: <Sparkles size={18} color={colors.accent.purple[400]} strokeWidth={2.2} />,
  save: <Save size={18} color={colors.status.success.main} strokeWidth={2.2} />,
};

const tileTint: Record<NodeType, { bg: string; border: string }> = {
  trigger: { bg: colors.primary.muted, border: colors.primary.subtle },
  action: { bg: colors.accent.purple.gradientEnd, border: colors.accent.purple.gradientStart },
  condition: { bg: colors.status.info.bg, border: colors.status.info.main },
  output: { bg: colors.status.success.bg, border: colors.status.success.gradientStart },
};

const BADGE = 24;
const TILE = 36;

function StepBadge({ index, isActive }: Readonly<{ index: number; isActive: boolean }>) {
  return (
    <View style={[styles.stepBadge, isActive && styles.stepBadgeActive]}>
      <Text style={[styles.stepBadgeText, isActive && styles.stepBadgeTextActive]}>
        {String(index + 1).padStart(2, '0')}
      </Text>
    </View>
  );
}

function IconTile({ node, isActive }: Readonly<{ node: VisualizerNode; isActive: boolean }>) {
  const tint = tileTint[node.type];
  return (
    <View
      style={[
        styles.tile,
        { backgroundColor: tint.bg, borderColor: isActive ? tint.border : colors.border.muted },
      ]}
    >
      {nodeIcons[node.id] ?? <Play size={18} color={colors.primary.neon} strokeWidth={2.2} />}
    </View>
  );
}

export function WorkflowVisualizer({
  name,
  description,
  nodes,
  delay = 0,
}: Readonly<WorkflowVisualizerProps>) {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;

  const displayNodes =
    nodes ??
    defaultNodeKeys.map((node) => ({ id: node.id, type: node.type, label: t(node.labelKey) }));

  return (
    <AnimatedCard delay={delay}>
      <View style={styles.card}>
        <View style={styles.header}>
          <View style={styles.headerBadge}>
            <View style={styles.headerBadgeDot} />
            <Text style={styles.headerBadgeText}>PIPELINE</Text>
          </View>
          <Text variant="h5" color={colors.text.primary} style={styles.title}>
            {name}
          </Text>
          <Text variant="bodySmall" color={colors.text.secondary} style={styles.description}>
            {description}
          </Text>
        </View>

        <View style={styles.diagram}>
          {isDesktop ? (
            <HStack spacing="none" align="center" justify="center">
              {displayNodes.map((node, i) => (
                <Fragment key={node.id}>
                  <VStack spacing="sm" align="center" style={styles.desktopColumn}>
                    <StepBadge index={i} isActive={i === 0} />
                    <IconTile node={node} isActive={i === 0} />
                    <Text
                      variant="caption"
                      color={colors.text.secondary}
                      style={styles.desktopLabel}
                      numberOfLines={2}
                    >
                      {node.label}
                    </Text>
                  </VStack>
                  {i < displayNodes.length - 1 && (
                    <View style={styles.desktopConnector}>
                      <View style={styles.desktopLine} />
                      <ChevronRight
                        size={14}
                        color={colors.text.tertiary}
                        strokeWidth={2.2}
                        style={styles.desktopChevron}
                      />
                      <View style={styles.desktopLine} />
                    </View>
                  )}
                </Fragment>
              ))}
            </HStack>
          ) : (
            <View style={styles.timeline}>
              {displayNodes.map((node, i) => (
                <Fragment key={node.id}>
                  <View style={styles.timelineRow}>
                    <StepBadge index={i} isActive={i === 0} />
                    <View style={styles.timelineTile}>
                      <IconTile node={node} isActive={i === 0} />
                    </View>
                    <Text
                      variant="body"
                      color={colors.text.primary}
                      style={styles.timelineLabel}
                      numberOfLines={1}
                    >
                      {node.label}
                    </Text>
                  </View>
                  {i < displayNodes.length - 1 && <View style={styles.timelineGapLine} />}
                </Fragment>
              ))}
            </View>
          )}
        </View>
      </View>
    </AnimatedCard>
  );
}

const ROW_HEIGHT = TILE; // each timeline row aligns badge + tile to TILE height
const ROW_GAP = tokens.spacing.mdPlus;

const styles = StyleSheet.create({
  // Match WorkflowListItem's surface so the visualizer reads as the same
  // family of card on the screen.
  card: {
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.default,
    overflow: 'hidden',
  },
  header: {
    paddingHorizontal: tokens.spacing.md,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.md,
    gap: 6,
  },
  headerBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: colors.primary.muted,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  headerBadgeDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.primary.neon,
  },
  headerBadgeText: {
    fontSize: Platform.select({ web: 10, default: 9 }),
    fontWeight: '700',
    color: colors.primary.neon,
    letterSpacing: 1,
  },
  title: {
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  description: {
    lineHeight: 18,
  },

  diagram: {
    paddingHorizontal: tokens.spacing.md,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border.subtle,
  },

  // Step number badge — sits ON the rail (solid background covers the line behind it)
  stepBadge: {
    width: BADGE,
    height: BADGE,
    borderRadius: BADGE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  stepBadgeActive: {
    backgroundColor: colors.primary.muted,
    borderColor: colors.primary.neon,
  },
  stepBadgeText: {
    fontSize: 9,
    fontWeight: '700',
    color: colors.text.tertiary,
    letterSpacing: 0.5,
  },
  stepBadgeTextActive: {
    color: colors.primary.neon,
  },

  // Icon tile — small square with type-tinted bg and a node icon
  tile: {
    width: TILE,
    height: TILE,
    borderRadius: tokens.radius.smPlus,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Vertical timeline (mobile / tablet)
  timeline: {
    // no relative positioning — line segments render in the flow
  },
  timelineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: ROW_HEIGHT,
  },
  // 1.5px line segment sitting in the gap between two adjacent rows, aligned
  // with the badge column center. Avoids the absolute-rail approach that
  // required the badge bg to opaquely cover the line (broke on the
  // semi-transparent card surface).
  timelineGapLine: {
    width: 1.5,
    height: ROW_GAP,
    backgroundColor: colors.border.muted,
    marginLeft: BADGE / 2 - 0.75,
  },
  timelineTile: {
    marginLeft: tokens.spacing.md,
  },
  timelineLabel: {
    marginLeft: tokens.spacing.md,
    flex: 1,
    fontWeight: '600',
    fontSize: tokens.fontSize.base,
  },

  // Horizontal flow (desktop)
  desktopColumn: {
    width: 96,
  },
  desktopLabel: {
    textAlign: 'center',
    maxWidth: 92,
    minHeight: 32,
    lineHeight: 16,
    fontWeight: '500',
  },
  desktopConnector: {
    flexDirection: 'row',
    alignItems: 'center',
    // Align with the icon tile vertically: top stack item is the BADGE (24h)
    // + sm gap (10) so the tile center sits at BADGE + 10 + TILE/2 from the
    // column top. We translate the connector down so the chevron lands at
    // that same vertical anchor.
    marginTop: BADGE + 10,
    height: TILE,
    minWidth: 28,
  },
  desktopLine: {
    flex: 1,
    height: 1.5,
    backgroundColor: colors.border.muted,
  },
  desktopChevron: {
    marginHorizontal: 4,
  },
});
